<template>
  <div>
    <filters v-model="filters" />
    <list-items
      :items="productItems"
      :sort="filters.sort"
      :order="filters.order"
      @setOrder="setFilters"
    />
    <c-loading v-if="!vaultsStats.result || !stakeStats.result"></c-loading>
  </div>
</template>

<script>
import BN from "bignumber.js";
import { mapGetters } from "vuex";
import Filters from "./Filters.vue";
import ListItems from "./ListItems.vue";
import { projectMapEco } from "@/common/config/ecosystems";

export default {
  components: {
    Filters,
    ListItems,
  },
  data: () => ({
    filters: {
      search: "",
      productType: "",
      ecosystem: "",
      legacy: false,
      sort: "product",
      order: -1,
    },
  }),
  computed: {
    ...mapGetters({
      stakeStats: "productStats/stakeStats",
      vaultsStats: "productStats/vaultsStats",
      ethereumStakingProjects: "projectConfig/ethereumStakingProjects",
      tendermintStakingProjects: "projectConfig/tendermintStakingProjects",
      solanaStakingProjects: "projectConfig/solanaStakingProjects",
      suiStakingProjects: "projectConfig/suiStakingProjects",
      stakeApyMap: "productStats/stakeApyMap",
      getTokenPrice: "appConfig/getTokenPriceInUsd",
      allVaults: "projectConfig/allVaults",
      apys: "productStats/apys",
    }),
    productItems() {
      if (!this.vaultsStats.result || !this.stakeStats.result) return [];
      let items = [];

      // defi
      this.allVaults.forEach(item => {
        const statItem = this.vaultsStats.result.find(
          vs =>
            vs.vaultType === item.vaultType &&
            vs.token.toLowerCase() === item.coin.toLowerCase()
        );
        if (!statItem) return;
        items.push({
          type: "vaults",
          productName: item.identity.toLowerCase(),
          tokenName: item.identity,
          logo: item.vaultIcon,
          text: item.text,
          price: this.getTokenPrice(item.coin),
          tvl: new BN(statItem.expectedReturn)
            .multipliedBy(this.getTokenPrice(item.coin))
            .toFixed(2, BN.ROUND_UP),
          apy:
            Number(this.apys.vaults?.[item.vaultType]?.[item.coin]?.apy || 0) *
            100,
          market: projectMapEco.ethereum,
          migrationRequired: item.migrationRequired,
          withdrawalRequired: item.withdrawalRequired,
          isLegacy: item.isLegacy,
        });
      });
      [
        ...this.ethereumStakingProjects,
        ...this.solanaStakingProjects,
        ...this.suiStakingProjects,
        ...this.tendermintStakingProjects,
      ].forEach(project => {
        const stakeItem = this.stakeStats.result.find(
          ss =>
            ss.network === project.name ||
            ss.network === project.staking.network
        );
        if (project.name === 'kyber') {
          items.push({
            type: "stake",
            productName: project.name,
            tokenName: project.staking.token,
            logo:
              (this.$vuetify.theme.dark
                ? project.logoSmallDark
                : project.logoSmall) || project.logoSmall,
            text: project.text,
            price: this.getTokenPrice('knc'),
            tvl: '280042.29',
            apy: 1.88,
            market: projectMapEco[project.config.network],
            isLegacy: project.isLegacy,
          });
        }
        if (!stakeItem) {
          return;
        }
        items.push({
          type: "stake",
          productName: project.name,
          tokenName: project.staking.token,
          variesApy: project.variesApy,
          logo:
            (this.$vuetify.theme.dark
              ? project.logoSmallDark
              : project.logoSmall) || project.logoSmall,
          text: project.text,
          price: this.getTokenPrice(stakeItem.tokenName),
          tvl: new BN(Number(stakeItem.tokens) || 0)
            .multipliedBy(this.getTokenPrice(stakeItem.tokenName) || 0)
            .toFixed(2, BN.ROUND_UP),
          apy: Number(this.stakeApyMap[project.name]?.apy || 0) * 100,
          market: projectMapEco[project.config.network],
          isLegacy: project.isLegacy,
        });
      });
      return this.applyFilters(items);
    },
  },
  methods: {
    applyFilters(items = []) {
      const filteredItems = items.filter(item => {
        let searchMatching = true;
        let typeMatching = true;
        let ecosystemMatching = true;
        let legacyMatching = true;
        if (this.filters.search) {
          searchMatching = `${item.productName}-${item.tokenName}-${item.text}-${item.market.name}`
            .toLowerCase()
            .includes(this.filters.search.toLowerCase());
        }
        if (this.filters.productType?.length) {
          typeMatching = this.filters.productType.includes(item.type);
        }
        if (this.filters.ecosystem?.length) {
          ecosystemMatching =
            this.filters.ecosystem.includes(item.market.name) ||
            this.filters.ecosystem.includes(item.market.altName);
        }

        legacyMatching = this.filters.legacy ? item.isLegacy === true : item.isLegacy === undefined;
        return searchMatching && typeMatching && ecosystemMatching && legacyMatching;
      });
      return filteredItems.sort((a, b) => {
        switch (this.filters.sort) {
          case "product":
            return 0;
          case "apy":
            return (Number(a.apy) - Number(b.apy)) * this.filters.order;
          case "tvl":
            return (Number(a.tvl) - Number(b.tvl)) * this.filters.order;
          case "price":
            return (Number(a.price) - Number(b.price)) * this.filters.order;
          default:
            return 0;
        }
      });
    },
    setFilters(filters = {}) {
      this.filters = {
        ...this.filters,
        ...filters,
      };
    },
  },
};
</script>
